import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getProductDetails, getProductVariations, getProductReview } from "../../services/woocommerce";
import Layout from "../../components/Layout";
import { Star, ArrowLeft, Package, Tag, Store, Heart, Share2, ShoppingCart, MapPin, Phone, Mail, Award, ShieldCheck, TrendingUp } from "lucide-react";

const ProductView = () => {
    const { productId } = useParams<any>();
    console.log("Product ID:", productId);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [product, setProduct] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [variations, setVariations] = useState<any[]>([]);
    const [selectedVariation, setSelectedVariation] = useState<any | null>(null);
    const [isFavorite, setIsFavorite] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);
    const [reviews, setReviews] = useState<any[]>([]);

    const fetchProduct = async () => {
        try {
            setLoading(true);
            const response = await getProductDetails(Number(productId));
            const data = response.data || null;
            setProduct(data);
            setSelectedImage(data?.images?.[0]?.src || null);
            setError(null);
            if (data?.type === 'variable') {
                const variationRes = await getProductVariations(Number(productId));
                setVariations(variationRes.data || []);
            }
        } catch (err: any) {
            setError(err.message || "Failed to fetch product");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProduct();
    }, [productId]);

    const fetchReview = async () => {
        try {
            const params: any = {
                product: productId,
                per_page: 100,
            };

            const response = await getProductReview(params) as any;
            const reviewData = response.data || [];
            console.log("Review Data:", reviewData);
            
            // Filter reviews that match the current product ID
            const filteredReviews = reviewData.filter((review: any) => 
                review.product_id === Number(productId)
            );
            
            setReviews(filteredReviews);
        } catch (err: any) {
            console.error("Failed to fetch reviews:", err);
        }
    };

    useEffect(() => {
        fetchReview();
    }, [productId]);
    
    const displayPrice = (product: any, selectedVariation: any): string => {
        if (product.type === "variable") {
            if (selectedVariation && selectedVariation.price) {
                return `₹${selectedVariation.price}`;
            }
            return "Select a variation";
        } else if (product.type === "simple") {
            return product.price ? `₹${product.price}` : "Price not available";
        }
        return "Unknown product type";
    };

    if (loading) {
        return (
            <Layout>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center space-y-4">
                        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                        <p className="text-gray-600 text-lg font-medium">Loading product...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg shadow-lg max-w-md">
                        <p className="text-red-800 font-semibold">{error}</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!product) {
        return (
            <Layout>
                <div className="min-h-screen flex items-center justify-center">
                    <p className="text-gray-500 text-lg">No product data found.</p>
                </div>
            </Layout>
        );
    }

    const vendor = product.store;

    return (
        <Layout>
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50">
                {/* Navigation Bar */}
                <div className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-gray-100">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex items-center justify-between">
                            <button
                                onClick={() => navigate(-1)}
                                className="group flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-indigo-700 transform hover:-translate-y-0.5 transition-all duration-200"
                            >
                                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                                Back
                            </button>
                            {/* <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setIsFavorite(!isFavorite)}
                                    className={`p-3 rounded-full transition-all duration-200 ${isFavorite
                                        ? 'bg-red-500 text-white shadow-lg'
                                        : 'bg-white text-gray-600 hover:bg-red-50 shadow-md'
                                        }`}
                                >
                                    <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                                </button>
                                <button className="p-3 rounded-full bg-white text-gray-600 hover:bg-blue-50 shadow-md transition-all duration-200">
                                    <Share2 className="w-5 h-5" />
                                </button>
                            </div> */}
                        </div>
                    </div>
                </div>

                {/* Vendor Details Section */}
                {vendor && (
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                            {/* Vendor Banner */}
                            <div className="relative h-48 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 overflow-hidden">
                                {vendor.vendor_banner && (
                                    <img
                                        src={vendor.vendor_banner}
                                        alt="Vendor Banner"
                                        className="w-full h-full object-cover opacity-50"
                                    />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

                                {/* Vendor Logo */}
                                <div className="absolute -bottom-16 left-8">
                                    <div className="relative">
                                        <div className="w-32 h-32 rounded-2xl bg-white p-2 shadow-2xl ring-4 ring-white">
                                            {vendor.vendor_shop_logo ? (
                                                <img
                                                    src={vendor.vendor_shop_logo}
                                                    alt={vendor.vendor_shop_name}
                                                    className="w-full h-full object-contain rounded-xl"
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center text-white text-3xl font-bold">
                                                    {vendor.vendor_shop_name?.charAt(0) || 'S'}
                                                </div>
                                            )}
                                        </div>
                                        {vendor.disable_vendor === 'no' && (
                                            <div className="absolute -top-2 -right-2 bg-green-500 text-white p-1.5 rounded-full shadow-lg">
                                                <ShieldCheck className="w-5 h-5" />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Store Status Badge */}
                                {vendor.is_store_offline === 'no' && (
                                    <div className="absolute top-4 right-4">
                                        <div className="bg-green-500 text-white px-4 py-2 rounded-full font-semibold shadow-lg flex items-center gap-2">
                                            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                            Store Open
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="px-8 pt-20 pb-8">
                                {/* Vendor Header */}
                                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8">
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                                                {vendor.vendor_shop_name}
                                            </h2>
                                            {vendor.email_verified && (
                                                <div className="bg-blue-100 text-blue-600 p-2 rounded-full">
                                                    <Award className="w-5 h-5" />
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-lg text-gray-600 flex items-center gap-2">
                                            <Store className="w-5 h-5 text-blue-600" />
                                            Managed by <span className="font-semibold text-gray-900">{vendor.vendor_display_name}</span>
                                        </p>
                                        {vendor.store_rating && (
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-1 bg-yellow-100 px-3 py-1 rounded-full">
                                                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                                                    <span className="font-bold text-yellow-700">{vendor.store_rating}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 flex items-center gap-2">
                                        <Store className="w-5 h-5" />
                                        Visit Store
                                    </button>
                                </div>

                                {/* Vendor Description */}
                                {vendor.vendor_description && vendor.store_hide_description === 'no' && (
                                    <div className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-6 rounded-xl">
                                        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                                            <TrendingUp className="w-5 h-5 text-blue-600" />
                                            About the Store
                                        </h3>
                                        <div
                                            className="text-gray-700 leading-relaxed prose prose-sm max-w-none"
                                            dangerouslySetInnerHTML={{ __html: vendor.vendor_description }}
                                        />
                                    </div>
                                )}

                                {/* Contact Information Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {/* Email */}
                                    {vendor.vendor_email && vendor.store_hide_email === 'no' && (
                                        <div className="group bg-gradient-to-br from-pink-50 to-rose-50 hover:from-pink-100 hover:to-rose-100 p-6 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border-2 border-pink-100">
                                            <div className="flex items-start gap-4">
                                                <div className="bg-gradient-to-br from-pink-500 to-rose-500 p-3 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                                                    <Mail className="w-6 h-6 text-white" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-500 mb-1">Email</p>
                                                    <a
                                                        href={`mailto:${vendor.vendor_email}`}
                                                        className="text-sm font-semibold text-gray-900 hover:text-pink-600 transition-colors break-all"
                                                    >
                                                        {vendor.vendor_email}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Phone */}
                                    {vendor.vendor_phone && vendor.store_hide_phone === 'no' && (
                                        <div className="group bg-gradient-to-br from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 p-6 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border-2 border-green-100">
                                            <div className="flex items-start gap-4">
                                                <div className="bg-gradient-to-br from-green-500 to-emerald-500 p-3 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                                                    <Phone className="w-6 h-6 text-white" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-500 mb-1">Phone</p>
                                                    <a
                                                        href={`tel:${vendor.vendor_phone}`}
                                                        className="text-sm font-semibold text-gray-900 hover:text-green-600 transition-colors"
                                                    >
                                                        {vendor.vendor_phone}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Address */}
                                    {vendor.vendor_address && vendor.store_hide_address === 'no' && (
                                        <div className="group bg-gradient-to-br from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 p-6 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border-2 border-blue-100">
                                            <div className="flex items-start gap-4">
                                                <div className="bg-gradient-to-br from-blue-500 to-indigo-500 p-3 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                                                    <MapPin className="w-6 h-6 text-white" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-500 mb-1">Address</p>
                                                    <p className="text-sm font-semibold text-gray-900 leading-relaxed">
                                                        {vendor.vendor_address}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Trust Badges */}
                                <div className="mt-8 flex flex-wrap gap-3 justify-center">
                                    {vendor.disable_vendor === 'no' && (
                                        <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-full font-semibold shadow-md flex items-center gap-2">
                                            <ShieldCheck className="w-4 h-4" />
                                            Verified Seller
                                        </div>
                                    )}
                                    {vendor.is_store_offline === 'no' && (
                                        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-2 rounded-full font-semibold shadow-md flex items-center gap-2">
                                            <Store className="w-4 h-4" />
                                            Active Store
                                        </div>
                                    )}
                                    {vendor.email_verified && (
                                        <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-full font-semibold shadow-md flex items-center gap-2">
                                            <Award className="w-4 h-4" />
                                            Email Verified
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                        <div className="gap-0">
                            {/* Left: Image Gallery */}
                            <div className="p-8 lg:p-12 bg-gradient-to-br from-gray-50 to-gray-100">
                                <div className="sticky top-24 space-y-6">
                                    {/* Main Image */}
                                    <div className="relative group overflow-hidden rounded-2xl shadow-2xl bg-white">
                                        {imageLoading && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                                                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                            </div>
                                        )}
                                        {selectedImage && (
                                            <img
                                                src={selectedImage}
                                                alt="Selected Product"
                                                onLoad={() => setImageLoading(false)}
                                                className="w-full h-[400px] object-contain p-6 transform group-hover:scale-105 transition-transform duration-500"
                                            />
                                        )}
                                        {product.on_sale && (
                                            <div className="absolute top-4 right-4 bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 py-2 rounded-full font-bold shadow-lg animate-pulse">
                                                SALE!
                                            </div>
                                        )}
                                    </div>

                                    {/* Thumbnail Gallery */}
                                    <div className="grid grid-cols-5 gap-3">
                                        {product.images?.map((img: any, index: number) => (
                                            <button
                                                key={img.id}
                                                onClick={() => {
                                                    setSelectedImage(img.src);
                                                    setImageLoading(true);
                                                }}
                                                className={`relative group h-64 rounded-xl overflow-hidden transition-all duration-300 ${selectedImage === img.src
                                                    ? 'ring-4 ring-blue-500 shadow-lg scale-105'
                                                    : 'ring-2 ring-gray-200 hover:ring-blue-300 hover:scale-105'
                                                    }`}
                                            >
                                                <img
                                                    src={img.src}
                                                    alt={img.alt || product.name}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className={`absolute inset-0 bg-blue-500 transition-opacity ${selectedImage === img.src ? 'opacity-0' : 'opacity-0 group-hover:opacity-20'
                                                    }`}></div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right: Product Details */}
                        <div className="p-8 lg:p-12 space-y-8">
                            {/* Product Title */}
                            <div className="space-y-4">
                                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                                    {product.name}
                                </h1>

                                {/* Rating & Stock */}
                                <div className="flex items-center gap-6 flex-wrap">
                                    {product.average_rating && (
                                        <div className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-4 py-2 rounded-full shadow-md">
                                            <Star className="w-5 h-5 fill-current" />
                                            <span className="font-bold text-lg">{product.average_rating}</span>
                                            <span className="text-sm">/ 5</span>
                                        </div>
                                    )}
                                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold shadow-md ${product.stock_status === 'instock'
                                        ? 'bg-gradient-to-r from-green-400 to-emerald-500 text-white'
                                        : 'bg-gradient-to-r from-red-400 to-rose-500 text-white'
                                        }`}>
                                        <Package className="w-4 h-4" />
                                        {product.stock_status === 'instock' ? 'In Stock' : 'Out of Stock'}
                                    </div>
                                </div>
                            </div>

                            {/* Price Display */}
                            <div className="relative">
                                <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 text-white p-6 rounded-2xl shadow-xl">
                                    <p className="text-sm font-medium opacity-90 mb-1">Price</p>
                                    <p className="text-4xl font-bold">
                                        {displayPrice(product, selectedVariation)}
                                    </p>
                                </div>
                            </div>

                            {/* Variation Selector */}
                            {product.type === 'variable' && (
                                <div className="space-y-3">
                                    <label className="flex items-center gap-2 font-semibold text-gray-800 text-lg">
                                        <Tag className="w-5 h-5 text-blue-600" />
                                        Select Variation
                                    </label>

                                    <select
                                        value={selectedVariation?.id || variations[0]?.id || ""}
                                        onChange={(e) =>
                                            setSelectedVariation(
                                                variations.find((v: any) => v.id === Number(e.target.value))
                                            )
                                        }
                                        className="w-full border-2 border-gray-200 rounded-xl px-5 py-4 text-lg focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all outline-none bg-white shadow-sm hover:border-blue-300"
                                    >
                                        {variations.map((variation: any) => (
                                            <option key={variation.id} value={variation.id}>
                                                {variation.attributes.map((attr: any) => attr.option).join(" / ")} - ₹{variation.price}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Short Description */}
                            {product.short_description && (
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-6 rounded-xl">
                                    <div
                                        className="text-gray-700 leading-relaxed prose prose-sm"
                                        dangerouslySetInnerHTML={{ __html: product.short_description }}
                                    />
                                </div>
                            )}

                            {/* Product Meta Info */}
                            <div className="bg-gray-50 rounded-2xl p-6 space-y-3">
                                <h3 className="font-bold text-lg text-gray-900 mb-4">Product Information</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {product.sku && (
                                        <div className="flex items-start gap-3">
                                            <div className="bg-blue-100 p-2 rounded-lg">
                                                <Tag className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 font-medium">SKU</p>
                                                <p className="text-sm font-semibold text-gray-900">{product.sku}</p>
                                            </div>
                                        </div>
                                    )}
                                    {product.categories?.length > 0 && (
                                        <div className="flex items-start gap-3">
                                            <div className="bg-purple-100 p-2 rounded-lg">
                                                <Package className="w-4 h-4 text-purple-600" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 font-medium">Categories</p>
                                                <p className="text-sm font-semibold text-gray-900">
                                                    {product.categories.map((c: any) => c.name).join(", ")}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Full Description */}
                            {product.description && (
                                <div className="border-t-2 border-gray-100 pt-8 space-y-4">
                                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                                        <div className="w-1 h-8 bg-gradient-to-b from-blue-600 to-indigo-600 rounded-full"></div>
                                        Product Description
                                    </h2>
                                    <div
                                        className="text-md text-gray-500"
                                        dangerouslySetInnerHTML={{ __html: product.description }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Reviews Section */}
                    {reviews.length > 0 && (
                        <div className="mt-8 bg-white rounded-3xl shadow-2xl overflow-hidden p-8 lg:p-12">
                            <div className="space-y-6">
                                {/* Reviews Header */}
                                <div className="flex items-center justify-between border-b-2 border-gray-100 pb-6">
                                    <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                                        <div className="w-1 h-8 bg-gradient-to-b from-yellow-400 to-orange-500 rounded-full"></div>
                                        Customer Reviews
                                    </h2>
                                    <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-5 py-2 rounded-full font-bold shadow-lg">
                                        {reviews.length} {reviews.length === 1 ? 'Review' : 'Reviews'}
                                    </div>
                                </div>

                                {/* Average Rating Summary */}
                                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-6 border-2 border-yellow-100">
                                    <div className="flex flex-col md:flex-row items-center gap-6">
                                        <div className="text-center">
                                            <div className="text-5xl font-bold text-gray-900 mb-2">
                                                {(reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)}
                                            </div>
                                            <div className="flex items-center justify-center gap-1 mb-1">
                                                {[...Array(5)].map((_, i) => (
                                                    <Star
                                                        key={i}
                                                        className={`w-5 h-5 ${
                                                            i < Math.round(reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length)
                                                                ? 'text-yellow-400 fill-current'
                                                                : 'text-gray-300'
                                                        }`}
                                                    />
                                                ))}
                                            </div>
                                            <p className="text-sm text-gray-600">Based on {reviews.length} reviews</p>
                                        </div>

                                        {/* Rating Distribution */}
                                        <div className="flex-1 w-full space-y-2">
                                            {[5, 4, 3, 2, 1].map((rating) => {
                                                const count = reviews.filter(r => r.rating === rating).length;
                                                const percentage = (count / reviews.length) * 100;
                                                return (
                                                    <div key={rating} className="flex items-center gap-3">
                                                        <span className="text-sm font-medium text-gray-700 w-12">{rating} star</span>
                                                        <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                                                            <div
                                                                className="h-full bg-gradient-to-r from-yellow-400 to-orange-400 transition-all duration-500"
                                                                style={{ width: `${percentage}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className="text-sm font-medium text-gray-600 w-12">{count}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                {/* Individual Reviews */}
                                <div className="space-y-4">
                                    {reviews.map((review) => (
                                        console.log("Rendering review:", review),
                                        <div
                                            key={review.id}
                                            className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-6 border-2 border-gray-100 hover:border-blue-200 transition-all duration-300 hover:shadow-lg"
                                        >
                                            <div className="flex items-start justify-between gap-4 mb-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        {/* Reviewer Avatar */}
                                                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md">
                                                            {review.reviewer.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <h4 className="font-bold text-gray-900">{review.reviewer}</h4>
                                                            <p className="text-sm text-gray-500">
                                                                {new Date(review.date_created).toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'long',
                                                                    day: 'numeric'
                                                                })}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Rating Stars */}
                                                <div className="flex items-center gap-1 bg-white px-3 py-1.5 rounded-full shadow-sm">
                                                    {[...Array(5)].map((_, i) => (
                                                        <Star
                                                            key={i}
                                                            className={`w-4 h-4 ${
                                                                i < review.rating
                                                                    ? 'text-yellow-400 fill-current'
                                                                    : 'text-gray-300'
                                                            }`}
                                                        />
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Review Content */}
                                            <div
                                                className="text-gray-700 leading-relaxed prose prose-sm max-w-none"
                                                dangerouslySetInnerHTML={{ __html: review.review }}
                                            />

                                            {/* Verified Badge */}
                                            {review.status === 'approved' && (
                                                <div className="mt-4 inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                                                    <ShieldCheck className="w-4 h-4" />
                                                    Approved Review
                                                </div>
                                            )}

                                            {/* Review Status */}
                                            {/* {review.verified && (
                                                <div className="mt-3 inline-flex items-center gap-2 text-sm text-gray-500">
                                                    <Award className="w-4 h-4 text-blue-500" />
                                                    Approved Review
                                                </div>
                                            )} */}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default ProductView;